import { translate } from '@/translation/translate.ts';
import { useState, useEffect, useRef, FC, Dispatch, SetStateAction } from 'react';
import { Offline, Online } from 'react-detect-offline';
import { PinPrompt } from './PinPrompt';
import { ICONS } from '../Icons';
import { useEventConfig } from '@/eventConfig/useEventConfig';
import { i18n } from '@/translation/TranslationProvider.tsx';
import { useStaffNotes } from './useStaffNotes';
import { STAFF_NOTES_MAX_LENGTH } from './staffNotes.constants';
import { getDevice } from '@/util/getDevice.ts';
import { useAppConfig } from '@/appConfig/useAppConfig.ts';

interface PanelProps {
  setSidePanelVisible: Dispatch<SetStateAction<boolean>>;
  sidePanelVisible: boolean;
}

/**
 * SidePanel
 *
 * Opened via click on logo.
 * If meta.pin is assigned a value in config, this panel will be locked and users will be prompted to access pin.
 * If meta.pin is set to empty, the Panel will be available immediately, without being locked.
 *
 * @returns React.Element
 */
export const SidePanel: FC<PanelProps> = ({ setSidePanelVisible, sidePanelVisible }) => {
  const { staffNotes, setStaffNotes } = useStaffNotes();
  const { version } = useAppConfig() ?? {};
  const { eventConfig } = useEventConfig();

  // state values and setters
  const pin = eventConfig?.meta?.pin;
  const [locked, setLocked] = useState(!!pin);
  const [timeoutId, setTimeoutId] = useState(0);

  const START_SLIDE_ID = 'home';
  const title = translate(eventConfig?.slides[START_SLIDE_ID]?.title ?? undefined);
  const subtitle = translate(eventConfig?.slides[START_SLIDE_ID]?.subtitle ?? undefined);
  const eventCode = eventConfig?.meta?.eventCode;
  const location = i18n.countries(eventConfig!.meta.country)[0].flag + ' ' + eventConfig!.meta.location;
  const isDemoVersion = eventConfig?.meta?.demo || window.location.href.indexOf('/demo') > -1;
  const device = getDevice();
  const pinTimeout = eventConfig?.meta?.pinTimeout ?? 60 * 1000;

  // Accessibility and focus management
  const contentRef = useRef<HTMLDivElement | null>(null);
  const lastFocusedBeforePanelRef = useRef<HTMLElement | null>(null);

  const getFocusableElements = (root: HTMLElement): HTMLElement[] => {
    const selector = [
      'a[href]',
      'area[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'button:not([disabled])',
      'iframe',
      'object',
      'embed',
      '[contenteditable]',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');
    const nodes = Array.from(root.querySelectorAll<HTMLElement>(selector));
    return nodes.filter((el) => {
      const rect = el.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 && !el.hasAttribute('disabled') && el.tabIndex !== -1;
    });
  };

  const closeLabel = translate(eventConfig?.translations?.close);
  const titleId = 'sidepanel-title';
  const subtitleId = 'sidepanel-subtitle';
  const panelAriaLabel = title || 'Side panel';

  // clear timeout if panel is being reopened
  if (timeoutId && sidePanelVisible) {
    clearTimeout(timeoutId);
  }

  // close panel and set timer to stay unlocked for 60 seconds
  const close = () => {
    setSidePanelVisible(false);
    const id: number = window.setTimeout(() => {
      setLocked(true);
    }, pinTimeout);
    setTimeoutId(id);
  };

  // Manage focus on open/close and trap focus while visible
  useEffect(() => {
    if (sidePanelVisible) {
      const active = document.activeElement as HTMLElement | null;
      lastFocusedBeforePanelRef.current = active && typeof active.focus === 'function' ? active : null;

      // Move focus into the panel with priority
      setTimeout(() => {
        const container = contentRef.current;
        if (!container) return;

        let el: HTMLElement | null = null;

        // Prefer staff-notes textarea when unlocked; then close button
        if (!locked) {
          el =
            container.querySelector<HTMLElement>('#staff-comment') ||
            container.querySelector<HTMLElement>('button.panel-close') ||
            null;
        }

        if (!el) {
          const focusables = getFocusableElements(container);
          el = focusables[0] ?? container;
        }

        if (el && typeof el.focus === 'function') {
          el.focus();
        }
      }, 0);
    } else {
      const el = lastFocusedBeforePanelRef.current;
      if (el && typeof el.focus === 'function') {
        setTimeout(() => {
          el.focus();
        }, 0);
      }
      lastFocusedBeforePanelRef.current = null;
    }
  }, [sidePanelVisible, locked]);

  const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (!sidePanelVisible) return;

    // Close on Escape
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close();
      return;
    }

    if (e.key !== 'Tab') return;

    const container = contentRef.current ?? e.currentTarget;
    const focusables = getFocusableElements(container);
    if (focusables.length === 0) {
      e.preventDefault();
      container.focus();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement as HTMLElement | null;
    const activeInside = active && container.contains(active);

    if (e.shiftKey) {
      if (!activeInside || active === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (!activeInside || active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  return (
    <div className="SidePanel">
      <div
        className="content"
        ref={contentRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={!locked && title ? titleId : undefined}
        aria-describedby={!locked && subtitle ? subtitleId : undefined}
        aria-label={panelAriaLabel}
        onKeyDown={handleKeyDown}
      >
        {
          // initially show pin prompt
          locked ? (
            <>
              <PinPrompt setIsLocked={setLocked} sidePanelVisible={sidePanelVisible} />
              <div className="mask" onClick={close}></div>
            </>
          ) : (
            // if pin is correct, show content of sidebar
            <>
              <div className="event-title" id={titleId}>
                {title}
              </div>
              <div className="event-subtitle" id={subtitleId}>
                {subtitle}
              </div>
              <div className="event-location">{location}</div>

              <div className="actions">
                <span className="event-code">{eventCode}</span>
              </div>

              {isDemoVersion && (
                <div className="section">
                  <div className="section-content">
                    <i className={ICONS.success} />
                    {translate(eventConfig?.translations?.volunteerBriefing)}
                  </div>
                </div>
              )}

              <div className="section">
                <h5 className="section-title">{translate(eventConfig?.translations?.notesAndAnnotations)}</h5>
                <div className="section-content">
                  <textarea
                    id="staff-comment"
                    placeholder={translate(eventConfig?.translations?.reservedNotes)}
                    value={staffNotes}
                    maxLength={STAFF_NOTES_MAX_LENGTH}
                    onChange={(e) => {
                      setStaffNotes(e.target.value);
                    }}
                  />
                </div>
              </div>

              <div className="section">
                <h5 className="section-title">{translate(eventConfig?.translations?.aboutThisApp)}</h5>
                <div className="section-content small">
                  <Online>
                    <p className="network">
                      <i className="fas fa-signal online" />{' '}
                      {translate(eventConfig?.translations?.connectionEstablished)}
                    </p>
                  </Online>
                  <Offline>
                    <p className="network">
                      <i className="fas fa-signal offline" /> {translate(eventConfig?.translations?.noNetworkAvailabl)}
                    </p>
                  </Offline>
                  <p>
                    <i className="fas fa-tablet-alt" />{' '}
                    {device ? device.name : translate(eventConfig?.translations?.deviceUnknown)}
                  </p>
                  <p>
                    <i className="fas fa-code-branch"></i> {version}
                  </p>
                </div>
              </div>
            </>
          )
        }

        <button
          className="panel-close"
          onClick={(e) => {
            if (!sidePanelVisible) {
              e.preventDefault();
              return;
            }
            close();
          }}
          aria-label={closeLabel}
          disabled={!sidePanelVisible}
          tabIndex={sidePanelVisible ? 0 : -1}
          aria-disabled={!sidePanelVisible}
        >
          <i className="fas fa-times" />
        </button>
      </div>
      <div className="mask" onClick={close}></div>
    </div>
  );
};
